import {Injectable} from '@angular/core';
import Reteno, {SetUserAttributesPayload} from 'cordova-plugin-reteno';
import {RETENO_ENABLE, RETENO_API_KEY, RETENO_DEBUG_MODE} from '../../../providers/config';
import {AlertController, ModalController, Platform, ToastController} from '@ionic/angular';
import {Color} from '@ionic/core';
import {AlertService} from '../alert/alert.service';
import {BehaviorSubject, takeUntil} from 'rxjs';
import {TranslocoService} from "@jsverse/transloco";
import {InfoComponentModal} from "../../shared/components/modals/info/info.component";
import {
    PushMessageComponentModal, PushNotification,
    PushNotificationButton
} from "../../shared/components/modals/push-message/push-message.component";
import {OSNotification} from "cordova-plugin-reteno/dist/OSNotification";

@Injectable({
    providedIn: 'root'
})
export class RetenoService {
    private activeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public active$ = this.activeSubject.asObservable();

    private uiTexts = {
        permissionMsg: 'Would you like to enable push notifications to stay updated with the latest news and offers?',
        btnYes: 'Yes',
        btnNo: 'No',
        openUrlButton: 'Go'
    };

    constructor(
        public alertService: AlertService,
        private toastController: ToastController,
        private alertCtrl: AlertController,
        private modalCtrl: ModalController,
        private platform: Platform,
        private translocoService: TranslocoService
    ) {


        this.translocoService.selectTranslate('pushAskMessage').subscribe(value => {
            this.uiTexts.permissionMsg = value;
        });
        this.translocoService.selectTranslate('yes').subscribe(value => {
            this.uiTexts.btnYes = value;
        });
        this.translocoService.selectTranslate('no').subscribe(value => {
            this.uiTexts.btnNo = value;
        });
        this.translocoService.selectTranslate('pushMessage.default_button_label').subscribe(value => {
            this.uiTexts.openUrlButton = value;
        });



    }

    private async toast(message: string, color: Color = 'success') {
        const toast = await this.toastController.create({
            message,
            duration: 2500,
            position: 'bottom',
            color,
        });

        await toast.present();
    }

    private isTimePassedSinceLastPermissionsAlert(): boolean {
        const lastAlertTime = localStorage.getItem('lastPermissionsAlertTime');

        if (!lastAlertTime) {
            return true;
        }

        const diff = Date.now() - parseInt(lastAlertTime, 10);
        const period = 7 * 24 * 60 * 60 * 1000; // 7 days

        return diff >= period;
    }

    private savePermissionsAlertTimestamp(): void {
        localStorage.setItem('lastPermissionsAlertTime', Date.now().toString());
    }

    private hasPushPermission(): boolean {
        return localStorage.getItem('pushPermissionGranted') === 'true';
    }

    private setPushPermissionGranted() {
        localStorage.setItem('pushPermissionGranted', 'true');
    }




    async askPermissions() {
        if (!this.activeSubject.getValue()) {
            throw new Error('Reteno SDK is not initialized');
        }

        if (this.hasPushPermission() || !this.isTimePassedSinceLastPermissionsAlert()) {
            return;
        }

        const alert = await this.alertCtrl.create({
            message: this.uiTexts.permissionMsg,
            buttons: [
                {
                    text: this.uiTexts.btnNo,
                    role: 'cancel',
                    handler: () => {

                        //console.log('Reteno: User declined push notifications');

                    }
                },
                {
                    text: this.uiTexts.btnYes,
                    handler: () => {
                        Reteno.Notifications.requestPermission(true).then((accepted: boolean) => {

                            if (accepted) {
                                this.setPushPermissionGranted();
                                if(RETENO_DEBUG_MODE) {
                                    this.toast('Reteno: User permissions accepted');
                                }
                            } else {
                                if(RETENO_DEBUG_MODE) {
                                    this.toast('Reteno: User permissions denied', 'danger');
                                }
                            }
                            // console.log('USER PERMISSIONS: ' + accepted);

                        })
                        .catch((err) => {

                            if(RETENO_DEBUG_MODE) {
                                this.toast('Reteno: User permissions error', 'danger');
                            }
                            console.log('USER PERMISSIONS ERROR: ', err);

                        });
                    }
                }
            ]
        });

        await alert.present();
        this.savePermissionsAlertTimestamp();
    }

    private async start() {
        if (this.activeSubject.getValue()) {
            return;
        }

        try {
            const response = await Reteno.initialize(RETENO_API_KEY, RETENO_DEBUG_MODE);

            console.log('START RESPONSE', response);

            this.activeSubject.next(true)

            Reteno.Notifications.addEventListener('click', (event) => {

                console.log('Reteno: Notification Click', event);

                let title = event.notification.title;

                if (event.result.actionId) {
                    //title += `[${event.result.actionId}]`;
                }

                this.showPushMessage(event.notification);
                // this.alertService.presentAlert(
                //     event.notification.body + '<img src="https://pics.esputnik.com/repository/home/104498/mobile-push/images/1744123616784.png">',
                //     title
                // );


            });

            Reteno.Notifications.addEventListener('foregroundWillDisplay', async (event) => {

                console.log('Reteno: Notification Will Display', event);
                console.log(event.getNotification());
                const notification = event.getNotification();

                this.showPushMessage(notification);

                // this.alertService.presentAlert(
                //     notification.body + '<img src="https://pics.esputnik.com/repository/home/104498/mobile-push/images/1744123616784.png">',
                //     notification.title
                // );

            });

            return true;
        } catch (err) {
            console.log('START ERROR', err);

            return false;
        }
    }

    async showPushMessage(notification: OSNotification) {

        const pushMessagePayload: PushNotification = {
            title: notification.title ?? "",
            body: notification.body,
            image: notification.bigPicture,
            buttons: []
        }

        let additionalData = notification.additionalData as any;
        if(additionalData){

            if(!notification.bigPicture && additionalData.es_notification_image){
                pushMessagePayload.image = additionalData.es_notification_image;
            }

            if(additionalData.link){

                if(!pushMessagePayload.buttons){
                    pushMessagePayload.buttons = [];
                }
                pushMessagePayload.buttons.push({
                    title: this.uiTexts.openUrlButton,
                    type: "url",
                    url: additionalData.link
                })

            }

        }


        const modal = await this.modalCtrl.create({
            component: PushMessageComponentModal,
            cssClass: 'auto-height push-message-modal',
            componentProps: {
                notification: pushMessagePayload
            }


        });

        await modal.present();

        const {data, role} = await modal.onWillDismiss();

    }

    initialize(userData: SetUserAttributesPayload) {
        if (!RETENO_ENABLE) {
            return;
        }

        if (!this.platform.is('hybrid')) {
            return;
        }

        this.platform.ready().then(async () => {
            const active = await this.start();

            if (!active) {
                return;
            }

            this.setUserAttributes(userData);
            await this.askPermissions();
        });
    }

    setUserAttributes(attributes: SetUserAttributesPayload) {
        if (!this.activeSubject.getValue()) {
            throw new Error('Reteno SDK is not initialized');
        }

        Reteno.setUserAttributes(attributes)
            .then((response: boolean) => {

                // this.toast('Reteno: success SetUserAttributes');
                console.log('Reteno: SetUserAttributes', response);

            })
            .catch(err => {

                // this.toast('Reteno: failed SetUserAttributes', 'danger');
                console.log('Reteno ERROR: SetUserAttributes', err);

            });
    }
}
